import { Injectable } from '@angular/core';
import { Institution, OptimizedLoansMappingQueryVariables } from '@lib/utils';
import { Apollo, gql, TypedDocumentNode } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ReportCalculatorRequestDaoModel } from '../dao/report-calculator-request.dao.model';
import { ReportCalculatorResponseDaoModel } from '../dao/report-calculator-response.dao.model';
import {
  AutocompleteInstitutions,
  CipSalaryAnalysisByZipCodeDocument,
  ExchangeAutoCompleteForLocationDocument,
  GenerateAdvice,
  InstitutionByUnitIdDocument,
  OptimizedLoansMappingDocument,
  UserReport
} from './queries/graph-queries';
import { AdviceInputDaoModel } from '@core/dao/advice-input.dao.model';
import { AdviceOutputDaoModel } from '@core/dao/advice-output.dao.model';

@Injectable()
export class GraphqlApi {
  public constructor(private apollo: Apollo) {}

  public query<T>(
    query: TypedDocumentNode<unknown, unknown>,
    variables: Record<string, unknown>,
    options?: Record<string, unknown>
  ): Observable<T> {
    return this.apollo
      .query({
        query,
        variables,
        ...options
      })
      .pipe(map(res => res.data as T));
  }

  public mutate<T>(
    mutation: TypedDocumentNode<unknown, unknown>,
    variables: Record<string, unknown>,
    options?: Record<string, unknown>
  ): Observable<T> {
    return this.apollo
      .mutate<T>({
        mutation,
        variables,
        ...options
      })
      .pipe(map(res => res.data as T));
  }

  public exchangeAutoCompleteForLocationGQL(
    input: Record<string, unknown>,
    context: Record<string, unknown>
  ): Observable<any> {
    return this.query(ExchangeAutoCompleteForLocationDocument, input, context);
  }

  public cipSalaryAnalysisByZipCodeGQL(
    input: Record<string, unknown>,
    context: Record<string, unknown>
  ): Observable<any> {
    return this.query(CipSalaryAnalysisByZipCodeDocument, input, context);
  }

  public institutionByUnitIdGQL(
    input: Record<string, unknown>,
    context: Record<string, unknown>
  ): Observable<Institution> {
    return this.query<{
      institution: Institution;
    }>(InstitutionByUnitIdDocument, input, context).pipe(map(value => value.institution));
  }

  public optimizedLoansMappingGQL(
    input: OptimizedLoansMappingQueryVariables,
    context: Record<string, unknown>
  ): Observable<any> {
    return this.query(OptimizedLoansMappingDocument, input as unknown as Record<string, unknown>, context);
  }

  public getAutocompleteInstitutions(
    keyword: string,
    options?: Record<string, unknown>
  ): Observable<OptionItemDaoModel[]> {
    return this.query<{
      autocompleteInstitutions: OptionItemDaoModel[];
    }>(AutocompleteInstitutions, { keyword }, options).pipe(map(value => value.autocompleteInstitutions || []));
  }

  public getUserReport(
    input: ReportCalculatorRequestDaoModel,
    options?: Record<string, unknown>
  ): Observable<ReportCalculatorResponseDaoModel> {
    return this.mutate<{
      calculateResult: {
        message?: string;
        data: ReportCalculatorResponseDaoModel;
      };
    }>(UserReport, { input }, options).pipe(
      map(response => {
        if (response.calculateResult?.message) {
          throw new Error(response.calculateResult.message);
        }
        return response.calculateResult.data;
      })
    );
  }

  public generateAdvice(
    questionId: number,
    input: AdviceInputDaoModel,
    options?: Record<string, unknown>
  ): Observable<AdviceOutputDaoModel> {
    return this.query<{
      generateAdvice: AdviceOutputDaoModel;
    }>(GenerateAdvice, { input, questionId }, options).pipe(map(response => response.generateAdvice));
  }
}
